/**
 * The drawing drawer (wave 2): clicking a row opens the drawing on the right — details, submissions
 * (log one in place, record the consultant's reply) and the activity log — while the list stays in
 * view. Edit / Add opens the same drawer as a form (unsaved edits are guarded by SidePanel).
 * Replaces the old full-width detail modal and the whole-table "Edit" mode.
 */
import { memo, useCallback, useMemo, useState } from 'react';
import { ExternalLink, Loader2, Pencil, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RowActions } from '@/components/ui/row-actions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { normalizeShopDrawingDisciplines } from '@/contexts/AppSettingsContext';
import { formatDate } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { getStatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  BLOCKERS,
  BLOCKER_LABELS,
  REPLIES,
  REPLY_LABELS,
  STATUSES,
  STATUS_LABELS,
  buildSubmissionRows,
  drawingTitle,
  fieldLabel,
  fieldValueText,
  labelize,
  projectNumber,
  replyLabel,
  REPLY_CODE_OF,
  REPLY_CODES,
  safeHref,
  todayIso,
} from './model';
import { AgeText, DisciplineChip, DrawingIdText, PersonChip, ReasonBadge, subClass } from './SdUi';
import { RecordReplyPopover, ReplyCodeBadge } from './ReplyForm';

const inputClass = 'h-9 w-full rounded-control border border-line bg-surface px-2.5 text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)] disabled:bg-surface-2 disabled:text-ink-3';
const labelClass = 'block text-[12px] font-semibold text-ink-2';
const selectContentClass = 'rounded-xl border-line bg-surface text-ink shadow-overlay';
const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/* ------------------------------------------------------------------ fields */

/** Text field. onChange(name, value) so one stable updater serves every field. */
export const Field = memo(function Field({ name, label, value, onChange, required = false, placeholder = '', disabled = false, type = 'text', max, list, testId, className }) {
  return (
    <label className={cn(labelClass, className)}>
      {label}{required ? <span className="text-status-red-ink"> *</span> : null}
      <input
        data-testid={testId || `shop-drawing-field-${slug(label)}`}
        type={type}
        max={max}
        list={list}
        required={required}
        disabled={disabled}
        value={value || ''}
        placeholder={placeholder}
        onChange={(event) => onChange(name, event.target.value)}
        className={cn(inputClass, 'mt-1 font-normal')}
      />
    </label>
  );
});

export const SelectField = memo(function SelectField({ name, label, value, values, labels = {}, onChange, toneDomain }) {
  return (
    <div className={labelClass}>
      <span id={`sd-select-${name}`}>{label}</span>
      <Select value={value} onValueChange={(next) => onChange(name, next)}>
        <SelectTrigger
          aria-labelledby={`sd-select-${name}`}
          data-testid={`shop-drawing-select-${slug(label)}`}
          className="mt-1 h-9 rounded-control border-line bg-surface text-[13px] font-normal text-ink shadow-none focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent className={selectContentClass}>
          {values.map((item) => (
            <SelectItem key={item} value={item} className="rounded-lg text-[13px]">
              {toneDomain ? (
                <span className="inline-flex items-center gap-2">
                  <span className={cn('ui-tone-dot', `tone-${getStatusTone(item, toneDomain)}`)} style={{ width: 8, height: 8 }} aria-hidden="true" />
                  {labelize(item, labels)}
                </span>
              ) : labelize(item, labels)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});

/** A PDF / DWG link — only http(s) or a path on this site opens; anything else shows as text. */
function LinkOut({ href: raw, label }) {
  const href = safeHref(raw);
  if (!href) return <span className="break-all text-[12.5px] text-ink-2" title="Not a web link">{label}: {String(raw || '').trim()}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline">
      {label} <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function Facts({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
      {items.filter(Boolean).map(([label, value, wide]) => (
        <div key={label} className={cn('min-w-0', wide && 'sm:col-span-2')}>
          <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-3">{label}</dt>
          <dd className="mt-1 min-w-0 break-words text-[13px] text-ink">{value === '' || value === null || value === undefined ? <span className="text-ink-4">—</span> : value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ submissions */

const createSubmissionDraft = (by = '', drawing = {}) => ({
  to: '',
  date: todayIso(),
  rev: drawing.rev || '',
  pdf: drawing.pdf || '',
  reply: 'PENDING',
  comments: '',
  by,
});

/** Log one submission on this drawing, in place (no dialog on top of the drawer). */
function SubmissionForm({ seed, onSave, onCancel }) {
  const [draft, setDraft] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const update = useCallback((field, value) => setDraft((current) => ({ ...current, [field]: value })), []);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not log the submission'));
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-line bg-surface-2 p-3.5" data-testid="submission-form">
      <h4 className="text-[13.5px] font-bold text-ink">Log new submission</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Sent to" value={draft.to} name="to" onChange={update} required placeholder="e.g. Consultant" />
        <Field label="Date sent" type="date" max={todayIso()} value={draft.date} name="date" onChange={update} required />
        <Field label="Revision sent" value={draft.rev} name="rev" onChange={update} />
        <Field label="PDF link" value={draft.pdf} name="pdf" onChange={update} />
        <SelectField label="Consultant reply" value={draft.reply} values={REPLIES} labels={REPLY_LABELS} name="reply" onChange={update} />
        <Field label="Sent by" testId="shop-drawing-field-your-name" value={draft.by} name="by" onChange={update} required />
      </div>
      <label className={labelClass}>
        Consultant comments
        <textarea
          value={draft.comments}
          onChange={(event) => update('comments', event.target.value)}
          className={cn(inputClass, 'mt-1 h-16 resize-y py-2 font-normal')}
        />
      </label>
      {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
          {saving ? 'Saving…' : 'Save submission'}
        </Button>
      </div>
    </form>
  );
}

function SubmissionCard({ row, onRecordReply }) {
  const { sent, submission } = row;
  const code = REPLY_CODE_OF[row.reply];
  const tone = row.replyState === 'awaiting' ? 'amber' : row.replyState === 'not_recorded' ? 'outline' : getStatusTone(row.reply, 'consultant_reply');
  return (
    <li className="rounded-xl border border-line bg-surface p-3" data-testid={`drawer-submission-${row.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-ink">
            {sent.label}
            {!sent.needsCheck && sent.age !== null ? <span className="font-normal text-ink-3"> · <AgeText days={sent.age} suffix=" ago" className="text-[12.5px]" /></span> : null}
            {sent.needsCheck ? <span className="text-[12px] font-semibold text-status-amber-ink"> · date needs check</span> : null}
          </p>
          <p className={subClass}>
            to {row.to || '—'} · by {row.by || '—'}{submission.rev ? ` · Rev ${submission.rev}` : ''}
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5">
          {code && row.replyState === 'replied' ? <SmallReplyBadge code={code} /> : null}
          <StatusPill tone={tone} label={row.replyState === 'replied' ? labelize(row.reply, REPLY_LABELS) : replyLabel(row)} size="sm" />
        </span>
      </div>
      {row.replyDate ? <p className="mt-1.5 text-[12px] text-ink-3">Reply recorded {row.replyDate.label}{submission.reply_by ? ` by ${submission.reply_by}` : ''}</p> : null}
      {row.importReply && row.replyState !== 'replied' ? <p className="mt-1.5 text-[12px] text-ink-3">The Excel import said {labelize(row.importReply, REPLY_LABELS)}; no reply was recorded.</p> : null}
      {row.replyComments || row.comments ? (
        <p className="mt-2 rounded-lg border-l-2 border-[color:var(--status-amber)] bg-status-amber-bg px-2.5 py-1.5 text-[12.5px] text-ink">{row.replyComments || row.comments}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {submission.pdf ? <LinkOut href={submission.pdf} label="PDF sent" /> : null}
        {row.replyState !== 'replied' && onRecordReply ? (
          <RecordReplyPopover rows={[row]} onSave={(form) => onRecordReply([row], form)} align="start" testId={`drawer-record-reply-${row.key}`} className="ml-auto" />
        ) : null}
      </div>
    </li>
  );
}

function SmallReplyBadge({ code }) {
  const item = REPLY_CODES.find((entry) => entry.code === code);
  return <ReplyCodeBadge code={code} tone={item?.tone || 'gray'} className="h-[20px] w-[20px] text-[10.5px]" />;
}

/* ------------------------------------------------------------------ activity */

const ENTRY_TONES = { create: 'blue', submit: 'amber', reply: 'teal', approve: 'navy', reject: 'red', edit: 'gray', chase: 'amber', info_request: 'blue' };

function TimelineEntry({ entry }) {
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      <span className={cn('ui-tone-dot mt-1.5 shrink-0', `tone-${ENTRY_TONES[entry.type] || 'gray'}`)} style={{ width: 9, height: 9 }} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-[13px] text-ink">{entry.note || 'Updated'}</p>
        <p className="mt-0.5 text-[12px] text-ink-3">{entry.who || '—'} · {entry.when ? formatDate(entry.when) : '—'}</p>
        {(entry.changes || []).length ? (
          <ul className="mt-1.5 space-y-1">
            {entry.changes.map((change, index) => (
              // The edit form's labels ('Project No.', 'Floor / Area'), not the stored keys ('project_no', 'floor').
              <li key={`${change.field}-${index}`} className="flex flex-wrap items-center gap-1.5 text-[12px]" data-testid="activity-change">
                <span className="font-semibold text-ink-2">{fieldLabel(change.field)}</span>
                <span className="rounded bg-surface-2 px-1.5 text-ink-3 line-through">{fieldValueText(change.field, change.from) || '—'}</span>
                <span className="text-ink-4" aria-hidden="true">→</span>
                <span className="sr-only">to</span>
                <span className="rounded bg-brand-soft px-1.5 font-semibold text-ink">{fieldValueText(change.field, change.to) || '—'}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {entry.comment ? <p className="mt-1.5 rounded-lg border-l-2 border-[color:var(--status-amber)] bg-status-amber-bg px-2.5 py-1.5 text-[12px] text-ink">{entry.comment}</p> : null}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ read drawer */

/*
  The drawer's write controls follow its callbacks (BUG-002): without `onEdit` no pencil, without
  `onSaveSubmission` no "Log new submission", without `onRecordReply` no "Record reply" on a submission, and the ⋯
  menu only when it has items — the page leaves them out for a role whose Shop Drawings level is View (or a Viewer).
*/

export function DrawingPanel({
  drawing,
  open,
  onOpenChange,
  onEdit,
  menuItems = [],
  onSaveSubmission,
  onRecordReply,
  currentUserName = '',
  submissionOpen = false,
  onSubmissionOpenChange,
}) {
  const rows = useMemo(() => (drawing ? buildSubmissionRows([drawing]) : []), [drawing]);
  if (!drawing) return null;
  const history = [...(drawing.history || [])].reverse();
  const projectNo = projectNumber(drawing);
  const subtitle = [
    drawing.project ? `${drawing.project}${projectNo ? ` #${projectNo}` : ''}` : 'No project',
    drawing.block,
    drawing.floor && drawing.floor !== drawingTitle(drawing) ? drawing.floor : '',
  ].map((value) => String(value || '').trim()).filter((value) => value && value !== '-').join(' · ');

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      modal={false}
      hideFooter
      title={drawingTitle(drawing)}
      subtitle={subtitle}
      status={<StatusPill status={drawing.status} domain="shop_drawing" label={labelize(drawing.status, STATUS_LABELS)} />}
      actions={(
        <>
          {onEdit ? (
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => onEdit(drawing)} aria-label="Edit drawing" title="Edit drawing" className="text-ink-3" data-testid="drawer-edit">
              <Pencil aria-hidden="true" />
            </Button>
          ) : null}
          {menuItems.length ? <RowActions label={`More actions for ${drawingTitle(drawing)}`} items={menuItems} data-testid="drawer-more" /> : null}
        </>
      )}
      testId="shop-drawing-drawer"
    >
      <SidePanelSection title="Details">
        <Facts items={[
          // A long ID breaks onto a second line instead of running under the Discipline beside it.
          ['Drawing ID', <DrawingIdText key="id" value={drawing.drawingId} wrap />],
          ['Discipline', <DisciplineChip key="disc" disc={drawing.disc} />],
          ['Type', drawing.dtype],
          ['Revision', drawing.rev],
          ['Responsible', <PersonChip key="resp" name={drawing.resp} />],
          ['Target date', drawing.target ? formatDate(drawing.target) : ''],
          ['Blocker', drawing.blocker && drawing.blocker !== 'NONE' ? <ReasonBadge key="reason" drawing={drawing} /> : (drawing.status === 'BLOCKED' ? <ReasonBadge key="reason" drawing={drawing} /> : '')],
          ['Internal no.', <span key="sd" className="font-mono text-[12px] text-ink-2">{drawing.id}</span>],
          ['Action note', drawing.action, true],
          drawing.notes ? ['Notes', <span key="notes" className="whitespace-pre-line">{drawing.notes}</span>, true] : null,
          (drawing.pdf || drawing.dwg) ? ['Files', (
            <span key="files" className="flex flex-wrap gap-3">
              {drawing.pdf ? <LinkOut href={drawing.pdf} label="PDF" /> : null}
              {drawing.dwg ? <LinkOut href={drawing.dwg} label="DWG" /> : null}
            </span>
          ), true] : null,
        ]} />
      </SidePanelSection>

      <SidePanelSection
        title={`Submissions${rows.length ? ` (${rows.length})` : ''}`}
        actions={onSaveSubmission && !submissionOpen ? (
          <Button type="button" variant="outline" size="xs" onClick={() => onSubmissionOpenChange?.(true)}>
            <Send aria-hidden="true" /> Log new submission
          </Button>
        ) : null}
      >
        {onSaveSubmission && submissionOpen ? (
          <SubmissionForm
            key={`submission-${drawing.id}`}
            seed={createSubmissionDraft(currentUserName, drawing)}
            onCancel={() => onSubmissionOpenChange?.(false)}
            onSave={async (draft) => {
              await onSaveSubmission(drawing, draft);
              onSubmissionOpenChange?.(false);
            }}
          />
        ) : null}
        {rows.length ? (
          <ul className="space-y-2">
            {[...rows].reverse().map((row) => <SubmissionCard key={row.key} row={row} onRecordReply={onRecordReply} />)}
          </ul>
        ) : !(onSaveSubmission && submissionOpen) ? <p className="text-[13px] text-ink-3">No submissions logged yet.</p> : null}
      </SidePanelSection>

      <SidePanelSection title="Activity">
        {history.length ? (
          <ol className="relative">{history.map((entry, index) => <TimelineEntry key={`${entry.when}-${index}`} entry={entry} />)}</ol>
        ) : <p className="text-[13px] text-ink-3">No activity yet.</p>}
      </SidePanelSection>
    </SidePanel>
  );
}

/* ------------------------------------------------------------------ add / edit drawer */

export const createDrawingDraft = () => ({
  project_no: '',
  project: '',
  disc: 'BMS',
  block: '',
  floor: '',
  dtype: 'Floor Plan',
  desc: '',
  drawingId: 'NO ID',
  rev: '',
  status: 'NOT_STARTED',
  blocker: 'NONE',
  action: '',
  resp: '',
  target: '',
  updby: '',
  pdf: '',
  dwg: '',
  notes: '',
  change_note: '',
});

const EDIT_FIELDS = ['project_no', 'project', 'disc', 'block', 'floor', 'dtype', 'desc', 'drawingId', 'rev', 'status', 'blocker', 'action', 'resp', 'target', 'pdf', 'dwg', 'notes'];

export function DrawingEditPanel({ open, onOpenChange, drawing, seed, disciplineOptions = [], drawingTypes = [], people = [], projects = [], currentUserName = '', onSave }) {
  const [draft, setDraft] = useState(seed);
  const update = useCallback((field, value) => setDraft((current) => ({ ...current, [field]: value })), []);
  const disciplines = useMemo(() => normalizeShopDrawingDisciplines([...(disciplineOptions || []), draft.disc]), [disciplineOptions, draft.disc]);
  // Discipline codes read as written (BMS, LCS, HA), not sentence case.
  const disciplineLabels = useMemo(() => Object.fromEntries(disciplines.map((value) => [value, String(value).replace(/_/g, ' ')])), [disciplines]);
  const changed = EDIT_FIELDS.filter((field) => String(draft[field] ?? '') !== String(seed[field] ?? ''));
  const dirty = drawing ? changed.length + (draft.change_note ? 1 : 0) : changed.length > 0;

  const save = async () => {
    if (!String(draft.desc || '').trim()) throw new Error('Unit description is required');
    if (!drawing) {
      await onSave(draft);
      return;
    }
    // An edit sends only the fields the person changed (+ the change note): a status set meanwhile
    // through the row's one-click menu is not overwritten by this form's older copy of it.
    await onSave({ ...Object.fromEntries(changed.map((field) => [field, draft[field]])), change_note: draft.change_note || '' });
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={drawing ? `Edit ${drawingTitle(drawing)}` : 'Add drawing'}
      subtitle={drawing ? `${drawing.id} · changes are kept in the drawing's activity log` : 'A new drawing in the register'}
      dirty={drawing ? changed.length : dirty}
      onSave={save}
      saveLabel="Save drawing"
      testId="shop-drawing-form"
    >
      <datalist id="sd-drawing-types">{drawingTypes.map((value) => <option key={value} value={value} />)}</datalist>
      <datalist id="sd-people">{people.map((value) => <option key={value} value={value} />)}</datalist>
      <datalist id="sd-projects">{projects.map((value) => <option key={value} value={value} />)}</datalist>
      <SidePanelSection title="Drawing">
        <Field label="Unit Description" value={draft.desc} name="desc" onChange={update} required />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Project" value={draft.project} name="project" onChange={update} list="sd-projects" />
          <Field label="Project No." value={draft.project_no} name="project_no" onChange={update} />
          <SelectField label="Discipline" value={draft.disc} values={disciplines} labels={disciplineLabels} name="disc" onChange={update} />
          <Field label="Drawing Type" value={draft.dtype} name="dtype" onChange={update} list="sd-drawing-types" />
          <Field label="Block / Zone" value={draft.block} name="block" onChange={update} />
          <Field label="Floor / Area" value={draft.floor} name="floor" onChange={update} />
          <Field label="Drawing ID" value={draft.drawingId} name="drawingId" onChange={update} />
          <Field label="Revision" value={draft.rev} name="rev" onChange={update} />
        </div>
      </SidePanelSection>
      <SidePanelSection title="Status & tracking">
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Status" value={draft.status} values={STATUSES} labels={STATUS_LABELS} name="status" onChange={update} toneDomain="shop_drawing" />
          <SelectField label="Blocker Type" value={draft.blocker} values={BLOCKERS} labels={BLOCKER_LABELS} name="blocker" onChange={update} />
          <Field label="Responsible Party" value={draft.resp} name="resp" onChange={update} list="sd-people" />
          <Field label="Target Date" value={draft.target} name="target" onChange={update} placeholder="DD/MM/YYYY" />
        </div>
        <Field label="Action / current state" value={draft.action} name="action" onChange={update} />
      </SidePanelSection>
      <SidePanelSection title="Files & notes" collapsible defaultOpen={Boolean(draft.pdf || draft.dwg || draft.notes)}>
        <Field label="PDF Link" value={draft.pdf} name="pdf" onChange={update} />
        <Field label="DWG Link" value={draft.dwg} name="dwg" onChange={update} />
        <label className={labelClass}>
          Notes
          <textarea value={draft.notes || ''} onChange={(event) => update('notes', event.target.value)} className={cn(inputClass, 'mt-1 h-20 resize-y py-2 font-normal')} />
        </label>
      </SidePanelSection>
      <SidePanelSection title="Audit">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Updated by" value={draft.updby || currentUserName} name="updby" onChange={update} required disabled={Boolean(currentUserName)} />
          <Field label="Change note" value={draft.change_note} name="change_note" onChange={update} placeholder="What did you change?" />
        </div>
      </SidePanelSection>
    </SidePanel>
  );
}
